import { isAxiosError } from 'axios';

import type { V1User } from '@/generated/hooks/agora/models';

/** The subset of Auth0 token claims that can carry a user's name. */
interface INameClaims {
  given_name?: string;
  family_name?: string;
}

/**
 * Whether onboarding still has to ask for the user's name — what `isNewUser`
 * gates across the flow.
 *
 * Both sources have to be empty. The `/me` record is absent mid-signup (it 403s
 * until the organization step creates it), and the token claims are absent on
 * enterprise IdPs and email-OTP, so reading either alone mis-routes one group.
 */
export function checkIsNewUser(
  user: V1User | undefined,
  tokenClaims: INameClaims | undefined,
): boolean {
  const hasRecordName = Boolean(user?.display_name?.trim());
  const hasTokenName = Boolean(
    tokenClaims?.given_name?.trim() && tokenClaims?.family_name?.trim(),
  );

  return !hasRecordName && !hasTokenName;
}

/**
 * Whether `/api/v1/me` answered "no record for this user yet" rather than
 * failing. Shared so the onboarding redirect and the invite flow can't drift on
 * what counts as a user who hasn't been created yet.
 */
export function checkHasNoUserRecord(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}
